import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Clock, Users, Calendar, CheckCircle2, XCircle, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';

import API from '../utils/api';

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

const PUNCH_STATUS_COLORS = {
  'working': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'left': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  'not_in': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

export default function PunchTrackerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/punch/team?date=${date}`, { withCredentials: true });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = date === todayStr;
  const summary = data?.summary || {};

  if (loading && !data) {
    return <SidebarLayout title="Punch Tracker" subtitle="Team Attendance"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Punch Tracker" subtitle="Live Team Attendance">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="punch-kpis">
        <div className="kpi-card border-l-4 border-purple-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Total Team</span>
          <div className="font-heading text-3xl font-black text-white mt-1">{summary.total || 0}</div>
        </div>
        <div className="kpi-card border-l-4 border-emerald-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Punched In</span>
          <div className="font-heading text-3xl font-black text-emerald-400 mt-1">{summary.punched_in || 0}</div>
          {isToday && <span className="text-[10px] text-emerald-500">Currently working</span>}
        </div>
        <div className="kpi-card border-l-4 border-slate-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Punched Out</span>
          <div className="font-heading text-3xl font-black text-slate-400 mt-1">{summary.punched_out || 0}</div>
        </div>
        <div className="kpi-card border-l-4 border-amber-500/20">
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Not In</span>
          <div className="font-heading text-3xl font-black text-amber-400 mt-1">{summary.not_in || 0}</div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">Date:</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          data-testid="punch-date-filter"
        />
        {!isToday && (
          <button onClick={() => setDate(todayStr)} className="text-[10px] px-2 py-1 rounded bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors" data-testid="punch-date-today">
            Back to Today
          </button>
        )}
        {isToday && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live — Auto-refreshes every 30s</span>}
      </div>

      {/* Team Table */}
      <div className="glass-card overflow-hidden" data-testid="punch-table">
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Status</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Hours</th>
            </tr>
          </thead>
          <tbody>
            {(!data?.logs || data.logs.length === 0) ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-500">No data for this date</td></tr>
            ) : data.logs.map(log => {
              const status = log.punch_out ? 'left' : log.punch_in ? 'working' : 'not_in';
              const statusLabel = status === 'working' ? 'Working' : status === 'left' ? 'Left' : 'Not In';
              return (
                <tr key={log.employee_id} data-testid={`punch-row-${log.employee_id}`}>
                  <td className="text-white text-sm font-medium">{log.employee_name}</td>
                  <td className="text-slate-400 text-xs">{log.department || '—'}</td>
                  <td>
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${PUNCH_STATUS_COLORS[status]}`}>
                      {status === 'working' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1 align-middle" />}
                      {statusLabel}
                    </span>
                  </td>
                  <td className="text-xs text-slate-300 font-mono">{formatTime(log.punch_in)}</td>
                  <td className="text-xs text-slate-300 font-mono">{formatTime(log.punch_out)}</td>
                  <td className="text-sm font-mono font-bold text-white">{log.total_hours ? `${log.total_hours.toFixed(1)}h` : log.punch_in && !log.punch_out ? '...' : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SidebarLayout>
  );
}
